import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { IconAlertTriangle, IconArrowLeft, IconBriefcase, IconCalendar, IconCalendarEvent, IconCheck, IconClock, IconCursorText, IconPlus, IconSparkles, IconUserPlus, IconX } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Page, PageHeader } from '@hana/shell';
import { Button, Chip, Drawer, Input, Modal, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { AREAS, AREA_LABELS, BARS, EXCEPTIONS, GPS, H0, H1, LANES, PENDING, PEOPLE_PINS, POINTS, TYPE_DOT, TYPE_LABEL, URGENT_CATALOG, hh, isUrgent, type Bar, type BarStatus, type Pending } from './data';
import s from './dispatch.module.css';
import './i18n';

// Mint-OpsDispatch (1536 / 1920): Lịch theo người · Bản đồ · Việc gấp · Ngoại lệ. Chỉ đọc; nút ghi giữ trạng thái cục bộ + toast. Không tiền.
// Mốc giờ / hạn đo bằng work-calendar ở bản nối dữ liệu thật (G-OD2); bản này dùng dữ liệu mẫu ngày 23/10/2026.

type Tab = 'grid' | 'map' | 'urg' | 'exc';
const KPI_ICON = [IconClock, null, null, null, IconCheck] as const;
const URG_TYPES = ['Lấy B/L gốc', 'Nộp LOI', 'Nộp chứng từ hãng tàu', 'Lấy lệnh D/O', 'Đóng phí / cược cont', 'Khác'] as const;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function Card({ title, note, noteOn, tools, children, foot, className }: { title: string; note?: string; noteOn?: boolean; tools?: ReactNode; children: ReactNode; foot?: string; className?: string }) {
  return (
    <section className={`${s.card} ${className ?? ''}`} aria-label={title}>
      <div className={s.cardHead}>
        <h2 className={s.cardTitle}>{title}</h2>
        {note && <span className={`${s.cardNote} ${noteOn ? s.cardNoteOn : ''}`}>{note}</span>}
        <span className={s.grow} />
        {tools && <div className={s.tools}>{tools}</div>}
      </div>
      {children}
      {foot && <div className={s.cardFoot}>{foot}</div>}
    </section>
  );
}

export function Dispatch() {
  const t = useT();
  const toast = useToast();
  const forced = readState();
  const [tab, setTab] = useState<Tab>('grid');
  const [day, setDay] = useState('23/10');
  const [pick, setPick] = useState<string | null>(null);
  const [asg, setAsg] = useState<Record<string, string>>({});
  const [extra, setExtra] = useState<Pending[]>([]);
  const [excDone, setExcDone] = useState<Record<string, boolean>>({});
  const [mf, setMf] = useState<'all' | 'pend' | 'urg' | 'svc'>('all');
  const [gp, setGp] = useState('KietLT');
  const [dock, setDock] = useState(false);
  const [dockX, setDockX] = useState(false);
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const [urgOpen, setUrgOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [urgType, setUrgType] = useState<string>(URG_TYPES[0]);

  const state: ScreenState = forced ?? 'ready';
  const ready = state === 'ready' || state === 'noperm';
  const noperm = state === 'noperm';

  const pend = useMemo(() => [...PENDING, ...extra].filter((p) => !asg[p.id]), [extra, asg]);
  const bars: Bar[] = useMemo(() => {
    const all = [...PENDING, ...extra];
    const made = Object.entries(asg).map(([id, who]): Bar => {
      const p = all.find((x) => x.id === id);
      const free = Math.max(8, ...BARS.filter((b) => b.who === who).map((b) => b.end));
      return { id, who, start: free, end: Math.min(20, free + 1.5), kind: p?.kind ?? 'OBL', job: p?.job ?? '', place: p?.place ?? '', status: 'Assigned' };
    });
    return [...BARS, ...made];
  }, [asg, extra]);
  const nUrg = pend.filter((p) => isUrgent(p.kind)).length;
  const excLeft = EXCEPTIONS.filter((e) => !excDone[e.id]).length;
  const noWrite = () => toast({ msg: t('opsd.noWrite') });
  const give = (id: string, who: string) => {
    setAsg((a) => ({ ...a, [id]: who }));
    setPick(null);
    toast({ msg: t('opsd.assigned', { id, who }) });
  };
  const dropOn = (who: string, off: boolean) => {
    if (!pick || noperm) return;
    if (off) toast({ msg: t('opsd.offLane', { who }) });
    else give(pick, who);
  };
  const createUrg = () => {
    const kind = ({ 'Lấy B/L gốc': 'OBL', 'Nộp LOI': 'LOI', 'Nộp chứng từ hãng tàu': 'NCT', 'Lấy lệnh D/O': 'DO', 'Đóng phí / cược cont': 'CUOC', Khác: 'OBL' } as const)[urgType as (typeof URG_TYPES)[number]];
    const id = `${kind}26100${30 + extra.length}`;
    setExtra((e) => [...e, { id, kind, job: 'SE261040501 · RUITONGVN-SC', place: 'HMM · Q1', due: '23/10 16:00', by: 'PHONGNT', suggest: 'KietLT', why: 'cùng khu Q1' }]);
    setUrgOpen(false);
    toast({ msg: `${id} · ${t('opsd.noWrite')}` });
  };

  const tabs: { key: Tab; label: string; n?: number; warn?: boolean }[] = [
    { key: 'grid', label: t('opsd.tab.grid'), n: pend.length, warn: pend.length > 0 },
    { key: 'map', label: t('opsd.tab.map') },
    { key: 'urg', label: t('opsd.tab.urg'), n: nUrg, warn: nUrg > 0 },
    { key: 'exc', label: t('opsd.tab.exc'), n: excLeft, warn: true },
  ];
  const allSeg = tabs.map((x) => ({ key: x.key, label: x.label, n: ready ? x.n : undefined }));

  const kpis: [string, string, string, string][] = [
    [t('opsd.kpi.pend'), String(pend.length), t('opsd.kpi.pendU', { n: nUrg }), pend.length ? 'var(--warn)' : 'var(--ok)'],
    [t('opsd.kpi.doing'), String(bars.filter((b) => b.status === 'Doing').length), t('opsd.kpi.doingU'), 'var(--tx)'],
    [t('opsd.kpi.late'), '1', 'NCT2610012 · trễ 25′', 'var(--err)'],
    [t('opsd.kpi.issue'), '1', 'KH2610034 · luồng đỏ', 'var(--warn)'],
    [t('opsd.kpi.done'), String(bars.filter((b) => b.status === 'Done').length), t('opsd.kpi.doneU'), 'var(--ok)'],
  ];

  const barCls = (b: Bar) => (b.status === 'Done' ? s.bDone : isUrgent(b.kind) ? s.bUrg : b.status === 'Doing' ? s.bDoing : s.bAssigned);

  const grid = (
    <>
      <div className={s.kpis}>
        {kpis.map(([k, v, u, c], i) => {
          const Ic = KPI_ICON[i];
          return (
            <button key={k} type="button" className={s.kpi} onClick={noWrite}>
              <span className={s.kpiK}>{Ic && <Ic size={16} aria-hidden="true" />}{k}</span>
              <span className={s.kpiRow}><span className={s.kpiV} style={{ color: c }}>{v}</span><span className={s.kpiU}>{u}</span></span>
            </button>
          );
        })}
      </div>
      <div className={s.cols}>
        <Card className={s.wait} title={t('opsd.wait')} note={pick ? t('opsd.picked', { id: pick }) : t('opsd.waitHint')} noteOn={!!pick}>
          {pend.map((p) => (
            <div key={p.id} className={s.item}>
              <span className={s.itemDot} style={{ background: pick === p.id ? 'var(--pri)' : TYPE_DOT[p.kind] }} aria-hidden="true" />
              <div className={s.itemBody}>
                <div className={s.itemK}>{TYPE_LABEL[p.kind]}{isUrgent(p.kind) ? ` · ${t('opsd.urgTag')}` : ''}</div>
                <div className={s.itemV}>{p.job.split(' · ')[1]} · {p.place} · {t('opsd.due', { d: p.due })} · {p.by}</div>
              </div>
              <div className={s.itemActs}>
                <button type="button" className={`${s.linkBtn} ${pick === p.id ? s.ghost : ''}`} disabled={noperm} onClick={() => setPick(pick === p.id ? null : p.id)}><IconCursorText size={14} aria-hidden="true" />{pick === p.id ? t('opsd.unpick') : t('opsd.pick')}</button>
                <button type="button" className={`${s.linkBtn} ${s.ghost}`} disabled={noperm} onClick={() => give(p.id, p.suggest)}><IconUserPlus size={14} aria-hidden="true" />{t('opsd.give', { who: p.suggest })}</button>
              </div>
            </div>
          ))}
          {pend.length === 0 && (
            <div className={s.item}>
              <span className={s.itemDot} style={{ background: 'var(--d-green)' }} aria-hidden="true" />
              <div className={s.itemBody}><div className={s.itemK}>Đã giao hết việc ngày {day}</div><div className={s.itemV}>Việc mới hiện ở đây khi job sinh dịch vụ hoặc có việc gấp</div></div>
            </div>
          )}
        </Card>
        <Card
          className={s.lanesCard}
          title={t('opsd.lanes', { d: `${day}/2026` })}
          note={pick ? t('opsd.dropHint', { id: pick }) : undefined}
          tools={
            <>
              <Button variant="secondary" size="sm" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => setUrgOpen(true)}>{t('opsd.newUrg')}</Button>
              <Button variant="secondary" size="sm" icon={<IconCalendar size={16} aria-hidden="true" />} onClick={() => setDay('23/10')}>{t('opsd.today')}</Button>
              <Button variant="secondary" size="sm" icon={<IconCalendarEvent size={16} aria-hidden="true" />} onClick={() => setDay('24/10')}>{t('opsd.tomorrow')}</Button>
            </>
          }
          foot={t('opsd.laneFoot')}
        >
          <div className={s.hours} aria-hidden="true">
            {[6, 8, 10, 12, 14, 16, 18].map((h) => <span key={h} className={s.hour} style={{ left: `${((h - H0) / (H1 - H0)) * 100}%` }}>{String(h).padStart(2, '0')}</span>)}
          </div>
          {LANES.map((l) => {
            const mine = bars.filter((b) => b.who === l.name);
            const hrs = mine.reduce((a, b) => a + (b.end - b.start), 0);
            return (
              <div key={l.name} className={s.lane}>
                <button type="button" className={`${s.laneHead} ${l.off ? s.laneHeadOff : ''} ${pick ? s.laneHeadPick : ''}`} aria-label={pick ? t('opsd.give', { who: l.name }) + ` ${pick}` : l.name} onClick={() => dropOn(l.name, l.off)}>
                  <span className={s.laneName}>{l.name}</span>
                  <span className={`${s.laneSub} ${l.off ? s.laneOff : ''}`}>{l.seat} · {t('opsd.jobs', { n: mine.length })} · {t('opsd.hours', { n: String(hrs).replace('.', ',') })}</span>
                </button>
                <div className={s.track}>
                  {mine.map((b) => (
                    <button key={b.id} type="button" className={`${s.bar} ${barCls(b)}`} style={{ left: `${((b.start - H0) / (H1 - H0)) * 100}%`, width: `${(Math.max(0.75, b.end - b.start) / (H1 - H0)) * 100}%` }} title={`${b.id} · ${TYPE_LABEL[b.kind]} · ${b.job} · ${b.place} · ${hh(b.start)}–${hh(b.end)} · ${t(`opsd.st.${b.status as BarStatus}`)}`} onClick={(e) => e.stopPropagation()}>
                      {b.end - b.start >= 2.5 ? `${TYPE_LABEL[b.kind]} · ${b.job.split(' · ')[1]}` : TYPE_LABEL[b.kind]}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </Card>
      </div>
    </>
  );

  const pts = POINTS.filter((p) => mf === 'all' || (mf === 'urg' ? isUrgent(p.kind) : mf === 'pend' ? /chưa giao/.test(p.v) : !isUrgent(p.kind)));
  const gpRows = GPS[gp] ?? [];
  const map = (
    <>
      <Card
        title={t('opsd.map.title', { d: day })}
        tools={
          <div className={s.chips}>
            {(['all', 'pend', 'urg', 'svc'] as const).map((k) => <button key={k} type="button" className={`${s.chipB} ${mf === k ? s.chipOn : ''}`} aria-pressed={mf === k} onClick={() => setMf(k)}>{t(`opsd.map.${k}`)}</button>)}
          </div>
        }
      >
        <div className={s.map} role="img" aria-label={t('opsd.map.aria')}>
          {AREA_LABELS.map((l) => <span key={l.k} className={s.mapLab} style={{ left: l.x, top: l.y }}>{l.k}</span>)}
          {pts.map((p) => <span key={p.k} className={s.pin} title={`${p.k} · ${TYPE_LABEL[p.kind]} · ${p.v}`} style={{ left: p.x, top: p.y, width: 13, height: 13, borderRadius: 3, background: /chưa giao/.test(p.v) ? 'var(--d-gray)' : TYPE_DOT[p.kind] }} />)}
          {PEOPLE_PINS.map((p) => <span key={p.k} className={s.pin} title={`${p.k} · ${t('opsd.map.checkin')}`} style={{ left: p.x, top: p.y, width: 14, height: 14, borderRadius: '50%', background: 'var(--d-green)' }} />)}
          <div className={s.legend}>
            {([[t('opsd.map.svc'), 'var(--d-blue)', '3px'], [t('opsd.map.urg'), 'var(--err)', '3px'], [t('opsd.map.pend'), 'var(--d-gray)', '3px'], [t('opsd.map.person'), 'var(--d-green)', '50%']] as const).map(([k, c, r]) => <span key={k}><i style={{ background: c, borderRadius: r }} />{k}</span>)}
          </div>
        </div>
      </Card>
      <Card title={t('opsd.area')} note={t('opsd.areaNote')} foot={t('opsd.areaFoot')}>
        <table className={s.table}>
          <thead><tr><th style={{ width: 160 }}>{t('opsd.colArea')}</th><th className={s.r} style={{ width: 80 }}>{t('opsd.colJobs')}</th><th className={s.r} style={{ width: 100 }}>{t('opsd.colPend')}</th><th>{t('opsd.colWho')}</th></tr></thead>
          <tbody>{AREAS.map(([k, n, p, w]) => <tr key={k}><td className={s.strong}>{k}</td><td className={s.r}>{n}</td><td className={s.r}>{p !== '0' ? <span className={s.pillWarn}>{p}</span> : <span className={s.lbTx}>0</span>}</td><td className={s.tx2}>{w}</td></tr>)}</tbody>
        </table>
      </Card>
      <Card
        title={t('opsd.gps', { n: gp })}
        note={t('opsd.gpsNote')}
        tools={<>{['ANHLT', 'AnNV', 'KietLT', 'ThangNV'].map((n) => <Button key={n} variant={gp === n ? 'secondary' : 'ghost'} size="sm" onClick={() => setGp(n)}>{n}</Button>)}</>}
        foot={t('opsd.gpsFoot')}
      >
        <table className={s.table}>
          <thead><tr><th style={{ width: 80 }}>{t('opsd.gcTime')}</th><th style={{ width: 130 }}>{t('opsd.gcEv')}</th><th style={{ width: 150 }}>{t('opsd.gcJob')}</th><th>{t('opsd.gcPlace')}</th><th className={s.r} style={{ width: 130 }}>{t('opsd.gcDev')}</th><th className={s.r} style={{ width: 140 }}>{t('opsd.gcKm')}</th><th style={{ width: 80 }}>{t('opsd.gcPh')}</th></tr></thead>
          <tbody>
            {gpRows.map(([h, ev, job, place, dev, km, ph]) => (
              <tr key={h + ev}>
                <td>{h}</td>
                <td>{ev === 'Báo sự cố' ? <span className={s.pillWarn}>{ev}</span> : <span className={s.strong}>{ev}</span>}</td>
                <td className={job === '—' ? s.lbTx : s.lk}>{job}</td>
                <td className={s.tx2}>{place}</td>
                <td className={s.r}>{parseInt(dev, 10) > 300 && /m$/.test(dev) ? <span className={s.pillErr}>{t('opsd.flag', { v: dev })}</span> : <span className={s.tx2}>{dev}</span>}</td>
                <td className={s.r}>{km}</td>
                <td className={s.tx2}>{ph === '—' ? '—' : t('opsd.photos', { n: ph })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );

  const urgRows = [...pend.filter((p) => isUrgent(p.kind)), { id: 'NCT2610012', kind: 'NCT', job: 'SE261040488 · GREENBUD', place: 'ONE · Q1 · nộp SI bản ký', due: '23/10 10:00', by: 'BKG NhiTHT', suggest: 'KietLT', why: 'đã giao' } as Pending];
  const urg = (
    <>
      <div className={s.noteBox}>
        <IconAlertTriangle size={20} aria-hidden="true" />
        <div style={{ flex: 1 }}><b>{t('opsd.urg.noteK')}</b><p>{t('opsd.urg.noteV')}</p></div>
        <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => setUrgOpen(true)}>{t('opsd.newUrg')}</Button>
      </div>
      <Card title={t('opsd.urg.today')} note={t('opsd.urg.n', { n: urgRows.length })} foot={t('opsd.urg.foot')}>
        <table className={s.table}>
          <thead><tr><th style={{ width: 170 }}>{t('opsd.uc.type')}</th><th style={{ width: 230 }}>{t('opsd.cJob')}</th><th>{t('opsd.cPlace')}</th><th style={{ width: 110 }}>{t('opsd.cDue')}</th><th style={{ width: 120 }}>{t('opsd.cBy')}</th><th style={{ width: 170 }}>{t('opsd.cGive')}</th></tr></thead>
          <tbody>
            {urgRows.map((p) => {
              const done = p.why === 'đã giao';
              return (
                <tr key={p.id} className={s.rowBtn} onClick={() => { setTab('grid'); setPick(done ? null : p.id); }}>
                  <td><span className={s.pillErr}>{TYPE_LABEL[p.kind]}</span></td>
                  <td className={s.lk}>{p.job}</td>
                  <td className={s.tx2}>{p.place}</td>
                  <td>{p.due}</td>
                  <td className={s.tx2}>{p.by}</td>
                  <td>{done ? <span className={s.pillOk}>{t('opsd.urg.given', { who: p.suggest })}</span> : <span className={`${s.warnTx} ${s.strong}`}>{t('opsd.urg.suggest', { who: p.suggest })}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      <Card title={t('opsd.uc.title')} note={t('opsd.uc.note')}>
        <table className={s.table}>
          <thead><tr><th style={{ width: 220 }}>{t('opsd.uc.type')}</th><th>{t('opsd.uc.where')}</th><th className={s.r} style={{ width: 110 }}>{t('opsd.uc.est')}</th><th>{t('opsd.uc.bring')}</th></tr></thead>
          <tbody>{URGENT_CATALOG.map(([k, w, h, m]) => <tr key={k}><td className={s.strong}>{k}</td><td className={s.tx2}>{w}</td><td className={s.r}>{h}</td><td className={s.tx2}>{m}</td></tr>)}</tbody>
        </table>
      </Card>
    </>
  );

  const excList = (
    <div>
      {EXCEPTIONS.map((e) => (
        <div key={e.id} className={s.item}>
          <span className={s.itemDot} style={{ background: excDone[e.id] ? 'var(--d-green)' : e.tone === 'err' ? 'var(--err)' : 'var(--d-amber)' }} aria-hidden="true" />
          <div className={s.itemBody}><div className={s.itemK}>{e.k}</div><div className={s.itemV}>{excDone[e.id] ? t('opsd.exc.done') : e.v}</div></div>
          {!excDone[e.id] && <div className={s.itemActs}><button type="button" className={s.linkBtn} disabled={noperm} onClick={() => setExcDone((d) => ({ ...d, [e.id]: true }))}>{e.act}</button></div>}
        </div>
      ))}
    </div>
  );
  const exc = (
    <>
      <Card title={t('opsd.exc.title')} note={t('opsd.exc.n', { n: excLeft })}>{excList}</Card>
      <div className={s.noteBox}><IconBriefcase size={20} aria-hidden="true" /><div><b>{t('opsd.cal.k')}</b><p>{t('opsd.cal.v')}</p></div></div>
    </>
  );

  const showDock = wide && !dockX && ready;
  const body = tab === 'grid' ? grid : tab === 'map' ? map : tab === 'urg' ? urg : exc;
  const askText = 'Ai rảnh ở Q1 trước 11:00 để lấy B/L HMM?';

  return (
    <Page kind="table">
      <PageHeader
        title={t('opsd.title')}
        lead={<button type="button" className={s.back} aria-label={t('opsd.back')} title={t('opsd.back')} onClick={() => history.back()}><IconArrowLeft size={18} stroke={1.9} aria-hidden="true" /></button>}
        meta={ready ? <><Chip>{t('opsd.chipOwner')}</Chip>{pend.length > 0 && <Chip tone="warn">{t('opsd.chipPend', { n: pend.length })}</Chip>}</> : undefined}
        sub={t('opsd.sub', { d: day, n: bars.length, p: LANES.length })}
        actions={
          <>
            <Button variant="secondary" aria-pressed={wide ? !dockX : dock} onClick={() => (wide ? setDockX((d) => !d) : setDock((d) => !d))}>{`${t('opsd.exc')} · ${excLeft}`}</Button>
            <Button variant="secondary" icon={<IconSparkles size={16} aria-hidden="true" />} onClick={() => setAskOpen(true)}>{t('opsd.ask')}</Button>
          </>
        }
      />
      <div className={`${s.wrap} ${showDock ? s.wrapDock : ''}`}>
      <div className={s.page}>
        <div><Segmented label={t('opsd.title')} value={tab} onChange={(k) => setTab(k as Tab)} items={allSeg.map((x) => ({ key: x.key, label: x.label, n: x.n }))} /></div>
        {noperm && <div role="note" className={s.note}><IconBriefcase size={16} aria-hidden="true" />{t('opsd.nopermNote')}</div>}
        <StateView
          state={state === 'noperm' ? 'ready' : state}
          title={state === 'empty' ? t('opsd.emptyT', { d: day }) : undefined}
          desc={state === 'empty' ? t('opsd.emptyD') : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          {body}
        </StateView>
      </div>
      {showDock && (
        <aside className={s.dockW} aria-label={t('opsd.exc')}>
          <div className={s.dockH}><b>{t('opsd.exc')}</b><button type="button" className={s.dockX} aria-label={t('opsd.dockClose')} title={t('opsd.dockClose')} onClick={() => setDockX(true)}><IconX size={16} aria-hidden="true" /></button></div>
          {excList}
        </aside>
      )}
      </div>
      <Modal
        open={urgOpen}
        onOpenChange={setUrgOpen}
        title={t('opsd.mdl.title')}
        description={t('opsd.mdl.desc')}
        footer={<><Button variant="secondary" onClick={() => setUrgOpen(false)}>{t('opsd.mdl.cancel')}</Button><Button variant="primary" onClick={createUrg}>{t('opsd.mdl.create')}</Button></>}
      >
        <div className={s.mdl}>
          <div className={s.opts} role="radiogroup" aria-label={t('opsd.mdl.type')}>
            {URG_TYPES.map((k) => <button key={k} type="button" role="radio" aria-checked={urgType === k} className={`${s.chipB} ${urgType === k ? s.chipOn : ''}`} onClick={() => setUrgType(k)}>{k}</button>)}
          </div>
          <Input label={t('opsd.mdl.job')} required defaultValue="SE261040501" placeholder="SE26…" />
          <Input label={t('opsd.mdl.place')} defaultValue="HMM · Q1" />
          <Input label={t('opsd.mdl.due')} required defaultValue="23/10 16:00" />
          <Input label={t('opsd.mdl.note')} />
        </div>
      </Modal>
      <Modal
        open={askOpen}
        onOpenChange={setAskOpen}
        title={t('opsd.ask.title')}
        description={t('opsd.ask.desc')}
        footer={<><Button variant="secondary" onClick={() => setAskOpen(false)}>{t('opsd.ask.close')}</Button><Button variant="primary" onClick={() => { give('OBL2610021', 'KietLT'); setAskOpen(false); }}>{t('opsd.give', { who: 'KietLT' })}</Button></>}
      >
        <div className={s.mdl}>
          <Input label={t('opsd.ask.q')} defaultValue={askText} />
          <p className={s.tx2}>KietLT xong nộp SI ONE dự kiến 11:00, cách HMM 600 m</p>
        </div>
      </Modal>
      <Drawer open={!wide && dock} onOpenChange={setDock} title={t('opsd.exc')}>{excList}</Drawer>
    </Page>
  );
}
